import { useEffect, useState } from "react";
import { Box, Button, Group, Loader, Select, Stack, Title } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  getAudienceTargets,
  getFleetSummary,
  listReleases,
  updateRelease,
} from "@utils/api-communication/HeadsetReleasesCli";
import type { AudienceTargets, FleetSummary, HeadsetRelease } from "types/headsetReleaseTypes";
import { FleetCard } from "./FleetCard";
import { notifyError } from "./notifyError";
import { canBeLatest } from "./releaseFormat";
import { ReleasesTable } from "./ReleasesTable";
import { NEW_RELEASE_PATH } from "./releaseRoutes";

export default function VersionManagementPage() {
  const { t } = useTranslation();
  const [releases, setReleases] = useState<HeadsetRelease[]>();
  const [fleet, setFleet] = useState<FleetSummary>();
  const [targets, setTargets] = useState<AudienceTargets>();

  useEffect(() => {
    listReleases().then(setReleases).catch(notifyError);
    getFleetSummary().then(setFleet).catch(notifyError);
    getAudienceTargets().then(setTargets).catch(notifyError);
  }, []);

  const latest = releases?.find((release) => release.isLatest);
  const changeLatest = async (version: string | null) => {
    if (!version || version === latest?.version) return;
    try {
      await updateRelease(version, { isLatest: true });
      setReleases(await listReleases());
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <Box p="lg">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <Title order={2}>{t("headsetReleases.title")}</Title>
          <Group gap="md" align="flex-end">
            <Select
              w={170}
              label={t("headsetReleases.latestForEveryone")}
              value={latest?.version ?? null}
              onChange={changeLatest}
              data={(releases ?? []).filter(canBeLatest).map((release) => release.version)}
              allowDeselect={false}
            />
            <Button component={Link} to={NEW_RELEASE_PATH} leftSection={<IconPlus size={16} />}>
              {t("headsetReleases.newRelease")}
            </Button>
          </Group>
        </Group>

        {!releases || !targets ? (
          <Loader />
        ) : (
          <>
            {fleet && <FleetCard summary={fleet} latestVersion={latest?.version} />}
            <ReleasesTable releases={releases} targets={targets} />
          </>
        )}
      </Stack>
    </Box>
  );
}
